import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { fetchPlacements } from 'core/actions/ocapi';
import isEmpty from 'lodash/isEmpty';
import { scrollToElement } from 'core/utils/helpers/scrollToElement';
import { selectIsMobileMQ } from 'core/modules/MediaQueries/selectors';
import ComponentErrorBoundary from 'core/modules/ErrorBoundaries/ComponentErrorBoundary';
import SectionTitle from 'core/modules/SectionTitle/SectionTitle';
import DataTable from 'core/modules/DataTable/DataTable';
import Sticky from 'core/modules/Sticky/Sticky';
import StickyContent from 'core/modules/Sticky/StickyContent';
import OfferTableItem from './components/OfferTableItem';
import Sort from '../Sort/Sort';
import { offerTableIsViewed } from './actions';

import './OfferTable.scss';

import {
  selectOfferTableConfig, selectOfferTableIsLoading, selectOfferTableIsLoaded,
  selectOfferTableData, selectOfferTableMetadata,
} from './selectors';

function OfferTable({
  section_title: dynamicTitle, title, content_group_id, namespace,
}) {
  const componentRef = useRef(null);
  const dispatch = useDispatch();

  const config = useSelector(selectOfferTableConfig);
  const isLoading = useSelector(selectOfferTableIsLoading);
  const isLoaded = useSelector(selectOfferTableIsLoaded);
  const data = useSelector(selectOfferTableData);
  const metadata = useSelector(selectOfferTableMetadata);
  const isMobileMQ = useSelector(selectIsMobileMQ);

  const {
    api: { params, options }, paginationOptions, sortOptions, sortOptionsDropdown,
  } = config;

  const [pageNumber, setPageNumber] = useState(paginationOptions.pageNumber);
  const [isExpandedSortView, setIsExpandedSortView] = useState(false);
  const [sortByDefault, setSortByDefault] = useState(params.sort_by);
  const [sortTypeDefault, setSortTypeDefault] = useState(params.sort_type);
  const [limitItems, setLimitItems] = useState(params.limit);

  const scrollToComponentRef = () => {
    if (componentRef.current) {
      setTimeout(() => { scrollToElement(componentRef.current); }, 500);
    }
  };

  const callFetchPlacements = (additionalParams, pageNumber) => {
    const {
      limit, sort_by: sortBy, sort_type: sortType,
    } = additionalParams;

    if (limit) {
      setLimitItems(limit);
    }

    if (pageNumber) {
      setPageNumber(pageNumber);
    }

    if (sortBy) {
      setSortByDefault(sortBy);
      setSortTypeDefault(sortType);
    }

    const paramsWithContentGroup = {
      content_group_id,
      ...params,
      limit: limit || limitItems,
      sort_by: sortBy || sortByDefault,
      sort_type: sortType || sortTypeDefault,
      ...additionalParams,
    };
    const { content_type_id, content_group_name } = paramsWithContentGroup;
    if (!paramsWithContentGroup.content_group_id && !content_type_id && !content_group_name) {
      return false; // One of the following params is required for /placements API endpoint
    }

    scrollToComponentRef();
    dispatch(fetchPlacements(paramsWithContentGroup, { ...options, namespace }));
    return true;
  };

  useEffect(() => {
    if (!isLoaded && !isLoading) {
      callFetchPlacements({}, pageNumber);
    }
  });

  useEffect(() => {
    const sectionTitle = dynamicTitle || title;
    if (data.length > 1 && metadata.date) {
      dispatch(offerTableIsViewed(data.length, sectionTitle, pageNumber));
    }
  }, [data, pageNumber, dispatch, dynamicTitle, metadata.date, title]);

  const handleSortButtonsTriggerClick = () => {
    setIsExpandedSortView(true);
  };

  const handleModifierChange = (modifiers) => {
    if (modifiers) {
      document.body.classList.remove('mn_addStickyHeightPadding');
    } else {
      document.body.classList.add('mn_addStickyHeightPadding');
    }

    setIsExpandedSortView(false);
  };

  const getOfferTableContent = (sectionId) => (
    data && data.map(dataItem => (
      <OfferTableItem
        key={dataItem.id}
        dataItem={dataItem}
        sectionId={sectionId}
      />
    ))
  );

  const columns = [{
    Header: '',
    accessor: 'id',
  }];

  const sortStickyContainerClass = isExpandedSortView ? 'mn_expanded' : '';
  const stickyOptions = { top: 80 };
  const sectionTitle = title || dynamicTitle;

  const renderSortingButtons = () => (
    <>
      <Sort
        data-test="offer-table-sort"
        sortOptions={sortOptions}
        fetchPlacementsMethod={callFetchPlacements}
      />
      <button className="mn_sortButtonsTrigger" onClick={handleSortButtonsTriggerClick}>
        Sort<span className="mn_sortButtonsTriggerIcon" />
      </button>
    </>
  );

  const renderSortingDropdown = () => (
    <Sort
      sortOptions={sortOptionsDropdown}
      sortType="dropdown"
      fetchPlacementsMethod={callFetchPlacements}
    />
  );

  if (isEmpty(data)) {
    return null;
  }

  return (
    <div
      className="mn_offerTable"
      data-test="offer-table"
      data-title={sectionTitle}
      ref={componentRef}
    >
      <SectionTitle title={sectionTitle} />
      <div className={`mn_content ${isLoading ? 'mn_contentIsLoading' : ''}`}>
        <Sticky sides={stickyOptions} offerTableRef={componentRef}>
          <StickyContent
            className={sortStickyContainerClass}
            handleModifierChange={handleModifierChange}
          >
            {isMobileMQ ? renderSortingButtons() : renderSortingDropdown()}
          </StickyContent>
        </Sticky>
        <DataTable
          columns={columns}
          data={data}
          data-test="offer-data-table"
          paginationOptions={paginationOptions}
          metadata={metadata}
          fetchPlacementsMethod={callFetchPlacements}
        >
          {getOfferTableContent(options.sectionId)}
        </DataTable>
      </div>
    </div>
  );
}

export default (props) => <ComponentErrorBoundary><OfferTable {...props} /></ComponentErrorBoundary>;
